import classNames from 'classnames'
import { format } from 'date-fns'
import Image from 'next/image'
import { n_content } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { buildSanityImage } from '../../../modules/utils'
import { getProgramStatus } from './ContentTypeUtils'

interface ReadProgramCardType {
  title: string
  thumbnail
  startDate: string
  endDate: string
  dueDate?: string
  onClickCard?: () => void
  isAvailable?: boolean | null
  isFinished?: boolean | null
  isReplay?: boolean | null
  tag?: string | null
  showDateInfo?: boolean
  isEnrolled?: boolean
}

const ReadProgramCard: React.FC<ReadProgramCardType> = ({
  title,
  thumbnail,
  startDate,
  endDate,
  dueDate,
  onClickCard,
  isAvailable = null,
  isFinished = null,
  isReplay = null,
  tag = null,
  showDateInfo = false,
  isEnrolled = false,
}) => {
  const { t } = useTranslation()

  const formatDate = (dateString: string) => {
    const parts = dateString.split('-')

    const year = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10)
    const day = parseInt(parts[2], 10)

    return `${year}.${month}.${day}`
  }

  return (
    <div onClick={() => onClickCard && onClickCard()}>
      <div className={style.cardWrapper}>
        <div>
          <div className="w-full">
            <Image
              alt={thumbnail.alt}
              loading="lazy"
              className={classNames('mx-auto aspect-[1.7083/1] rounded-[12px] object-cover')}
              src={buildSanityImage(thumbnail).url()}
              style={{
                width: '100%',
                height: '100%',
              }}
              width={336}
              height={196}
              quality={100}
            />
          </div>
        </div>
        <div className={style.descriptionSectionWrapper}>
          <div className={style.descriptionSection}>
            <div className={style.descriptionCategoryWrapper}>
              {getProgramStatus({
                t,
                isEnrolled,
                isAvailable,
                isFinished,
                isBeforeDueDate: new Date() < new Date(dueDate),
              })}
              {tag === null
                ? null
                : tag && (
                    <div className="Caption-12Bd rounded-[4px] bg-gray-100 px-[6px] pt-[2px] text-gray-600">{tag}</div>
                  )}
              {isReplay === null
                ? null
                : isReplay && (
                    <div className="Caption-12Bd rounded-[4px] bg-gray-600 px-[6px] pt-[2px] text-white">
                      {t(n_content.rwm.recorded)}
                    </div>
                  )}
            </div>

            <div className="flex h-full flex-col">
              <div className={style.descriptionTitle}>{title}</div>
              <div className="Body2-14Md text-gray-500">
                {showDateInfo && dueDate ? (
                  <div className="flex flex-col">
                    <div className="flex flex-row gap-10">
                      <div>{t(n_content.rwm.info_deadline)} :</div>
                      <div>{format(new Date(dueDate), 'yyyy.M.d h:mm a')}</div>
                    </div>
                    <div className="flex flex-row gap-10">
                      <div>{t(n_content.rwm.info_period)} :</div>
                      <div>
                        {formatDate(startDate)} ~ {formatDate(endDate)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="">
                    {formatDate(startDate)} ~ {formatDate(endDate)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const style = {
  cardWrapper: `relative w-full cursor-pointer overflow-hidden bg-white`,
  thumbnailWrapper: `relative overflow-hidden bg-gray-300`,
  descriptionSectionWrapper: `pt-[16px]`,
  descriptionSection: `flex h-full flex-col`,
  descriptionCategoryWrapper: `Body2-14Md flex flex-row items-center gap-[4px] text-gray-700`,
  descriptionTitle: `Body1-16Bd sm:Title1-20Bd mt-[12px] mb-4 max-h-[58px] truncate`,
  descriptionTagSection: `mt-[16px] flex flex-row gap-[8px]`,
  descriptionTags: `Caption-12Bd flex rounded-[4px] bg-gray-100 py-[2px] px-[6px] text-gray-600`,
  descriptionSubtitle: `truncate-2-lines keep-all Body2-14Md min-h-[42px] text-gray-700`,
}

export default ReadProgramCard

export const ReadProgramCardSkeleton = () => {
  return (
    <div className={classNames(style.cardWrapper, 'animate-pulse')}>
      <div>
        <div className="h-[192px] max-h-[60vh] w-full max-w-[100vw] rounded-[12px] bg-gray-100"></div>
      </div>
      <div className={style.descriptionSectionWrapper}>
        <div className={style.descriptionSection}>
          <div className={style.descriptionCategoryWrapper}>
            <div className="h-[23px] w-[44px] rounded-[4px] bg-gray-100"></div>
            <div className="h-[23px] w-[44px] rounded-[4px] bg-gray-100"></div>
          </div>
          <div className="flex h-full flex-col">
            <div className="mt-[8px] h-[29px] w-[328px] rounded-[4px] bg-gray-100"></div>
            <div className="Body2-14Md mt-[29px] h-[44px] w-[254px] rounded-[4px] bg-gray-100 text-gray-500"></div>
          </div>
        </div>
      </div>
    </div>
  )
}
